import React, { useEffect, useMemo, useState, Children, Component } from 'react';
/**
 * FeedbackSplash — Full-screen celebration/encouragement overlay.
 *
 * Shows after answering a question: confetti + Djay for correct,
 * warm floating particles + encouragement for incorrect.
 * Auto-advances after a configurable duration, tap to skip.
 *
 * App-Feel 2027 Upgrade: Richer, layered, more joyful redesign of BOTH the
 * correct and incorrect splash to match the cinematic SkinEvolutionReveal.
 */
import { motion, useReducedMotion } from 'framer-motion';
import { Star, Sparkles } from 'lucide-react';
import { DjayIdentity, getDjaySplashMessage, type DjayTier, type DjayVariant } from '../../../mascot';
import { playSoundIntent } from '../../../utils/sound/soundIntent';
import { hapticManager } from '../../../utils/hapticManager';
import { buildConfettiSet, WARM_PARTICLES, XP_COUNTER_STEPS, XP_COUNTER_DURATION_MS, buildSplashDisplayConfig, resolveSplashPriority, type ConfettiParticle, type WarmParticle } from './feedbackSplashData';
// ── Types ────────────────────────────────────────────────────
interface FeedbackSplashProps {
  isCorrect: boolean;
  xpGained: number;
  streak?: number;
  onComplete: () => void;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
  streakMilestone?: {
    count: number;
    message: string;
  } | null;
  dailyGoalReached?: boolean;
  tierEvolution?: {
    from: DjayTier;
    to: DjayTier;
  } | null;
  skinUnlock?: {
    skin: DjayVariant;
    description: string;
  } | null;
  /**
   * Active module's hex color (e.g. '#F97316'). When provided, the confetti
   * palette is derived from this hue so each world celebrates in its own
   * colors. App-Feel §16.E. Omit (or pass undefined) for cross-module / lab
   * sessions to fall back to the generic warm palette.
   */
  moduleColor?: string;
  familyMasteryPercent?: number;
  familyLabel?: string;
}
// ── Shared Sub-components ────────────────────────────────────
function RotatingRays({
  color,
  intensity = 1,
  reduced
}: {
  color: string;
  intensity?: number;
  reduced: boolean | null;
}) {
  if (reduced) return null;
  const rays = Array.from({
    length: 12
  }, (_, i) => i / 12 * 360);
  // Convert intensity to hex alpha (approx 0-255)
  const alphaHex = Math.min(255, Math.round(64 * intensity)).toString(16).padStart(2, '0');
  return <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-40" animate={{
    rotate: 360
  }} transition={{
    duration: 24 / intensity,
    ease: 'linear',
    repeat: Infinity
  }} data-id="element-10749">
      <div className="relative w-[140vmin] h-[140vmin]" data-id="element-10750">
        {rays.map((deg, i) => <div key={i} className="absolute left-1/2 top-1/2 origin-bottom" style={{
        width: '8vmin',
        height: '70vmin',
        transform: `translate(-50%, -100%) rotate(${deg}deg)`,
        background: `linear-gradient(to top, ${color}00, ${color}${alphaHex})`,
        opacity: i % 2 === 0 ? 1 : 0.6
      }} data-id="element-10751" />)}
      </div>
    </motion.div>;
}
function FloatingSparkles({
  reduced
}: {
  reduced: boolean | null;
}) {
  if (reduced) return null;
  const sparkles = Array.from({
    length: 8
  });
  return <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-20" data-id="element-10752">
      {sparkles.map((_, i) => {
      const angle = i / sparkles.length * Math.PI * 2;
      const distance = 120 + Math.random() * 80;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance - 40; // offset upwards
      return <motion.div key={i} className="absolute" initial={{
        opacity: 0,
        scale: 0,
        x: x * 0.5,
        y: y * 0.5
      }} animate={{
        opacity: [0, 1, 0],
        scale: [0, 1 + Math.random(), 0],
        x: x,
        y: y - 20
      }} transition={{
        duration: 2 + Math.random(),
        repeat: Infinity,
        delay: Math.random() * 2,
        ease: 'easeInOut'
      }} data-id="element-10753">
            <Sparkles className="w-5 h-5 text-white/90 fill-white/90" data-id="element-10754" />
          </motion.div>;
    })}
    </div>;
}
function ConfettiLayer({
  particles,
  reduced
}: {
  particles: ConfettiParticle[];
  reduced: boolean | null;
}) {
  if (reduced) return null;
  return <>
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.size} pointer-events-none opacity-80`} style={{
      left: `${p.startX}%`,
      top: `${p.startY}%`,
      backgroundColor: p.color
    }} initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      x: `${(p.endX - p.startX) * 2.5}vw`,
      y: `${p.endY - p.startY}vh`,
      scale: [0, 1.2, 1, 0.6],
      opacity: [0, 0.9, 0.9, 0],
      rotate: p.rotation
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      ease: 'easeOut'
    }} data-id="element-10755" />)}
    </>;
}
function WarmParticleLayer({
  particles,
  reduced
}: {
  particles: WarmParticle[];
  reduced: boolean | null;
}) {
  if (reduced) return null;
  return <>
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.size} pointer-events-none opacity-50`} style={{
      left: `${p.startX}%`,
      bottom: '0%',
      backgroundColor: p.color
    }} animate={{
      y: [`${p.startY}vh`, `${p.endY}vh`],
      x: [0, p.drift],
      opacity: [0, 0.5, 0.5, 0]
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-10756" />)}
    </>;
}
// ── Hooks ────────────────────────────────────────────────────
function useXpCounter(target: number) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (target === 0) return;
    const steps = XP_COUNTER_STEPS;
    const increment = target / steps;
    const interval = XP_COUNTER_DURATION_MS / steps;
    // App-Feel 2027 W11 — ONE swept rising sound for the whole count instead
    // of N rapid `playTick()`s (which could rattle on high XP). Fired once at
    // the start, synced to the counter's run via durationMs. The visual still
    // counts up step-by-step; only the audio is bundled.
    playSoundIntent('counter.sweep', {
      durationMs: XP_COUNTER_DURATION_MS
    });
    let current = 0;
    const id = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplay(target);
        clearInterval(id);
      } else {
        setDisplay(Math.floor(current));
      }
    }, interval);
    return () => clearInterval(id);
  }, [target]);
  return display;
}
// ── Component ────────────────────────────────────────────────
export function FeedbackSplash({
  isCorrect,
  xpGained,
  streak = 0,
  onComplete,
  levelUp,
  streakMilestone,
  dailyGoalReached,
  tierEvolution,
  skinUnlock,
  moduleColor,
  familyMasteryPercent,
  familyLabel
}: FeedbackSplashProps) {
  const shouldReduceMotion = useReducedMotion();
  const xpDisplay = useXpCounter(xpGained);
  const splashMsg = useMemo(() => getDjaySplashMessage(isCorrect, streak), [isCorrect, streak]);
  // App-Feel §16.E — confetti palette derives from the active module's hex.
  const confetti = useMemo(() => buildConfettiSet(moduleColor), [moduleColor]);
  // Play sound and haptic feedback on mount
  useEffect(() => {
    // App-Feel 2027 §3D/§8 — exactly ONE celebration sound per feedback
    // moment. Routing through the Sound-Intent SSOT means its `reward` cadans
    // (250ms dedupe) collapses any accidental double-fire. The XP counter now
    // emits a single swept `counter.sweep` intent (W11), so it can no longer
    // "steal" or stack over the reward slot.
    if (levelUp) {
      playSoundIntent('reward.levelUp');
      hapticManager.hapticCelebration();
    } else if (dailyGoalReached) {
      playSoundIntent('reward.goalReached');
      hapticManager.hapticCelebration();
    } else if (streakMilestone) {
      playSoundIntent('reward.streakMilestone');
      hapticManager.hapticCelebration();
    } else if (isCorrect) {
      playSoundIntent('answer.correct', {
        comboLevel: streak ?? 0
      });
      hapticManager.hapticSuccess();
    } else {
      playSoundIntent('answer.wrong');
      hapticManager.hapticError();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const config = useMemo(() => buildSplashDisplayConfig({
    isCorrect,
    streak,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
    splashMsg
  }), [isCorrect, streak, levelUp, tierEvolution, skinUnlock, dailyGoalReached, streakMilestone, splashMsg]);
  const priority = useMemo(() => resolveSplashPriority({
    isCorrect,
    streak,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
    splashMsg
  }), [isCorrect, streak, levelUp, tierEvolution, skinUnlock, dailyGoalReached, streakMilestone, splashMsg]);
  const {
    mainMessage,
    subMessage,
    emotion,
    duration,
    gradient,
    showExtraConfetti
  } = config;
  const noMotion = !!shouldReduceMotion;
  // Auto-advance timer
  useEffect(() => {
    const timer = setTimeout(onComplete, duration);
    return () => clearTimeout(timer);
  }, [duration, onComplete]);
  // ── Correct Splash ──
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      scale: noMotion ? 1 : 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: noMotion ? 1 : 1.05
    }} transition={{
      duration: 0.4,
      ease: [0.175, 0.885, 0.32, 1.275]
    }} onClick={onComplete} className="min-h-[calc(100vh-80px)] w-full flex flex-col items-center justify-center overflow-hidden cursor-pointer relative rounded-3xl" style={{
      background: gradient
    }} data-id="element-10757">
        <RotatingRays color={moduleColor || '#FBBF24'} intensity={streak >= 5 ? 1.5 : 1} reduced={shouldReduceMotion} data-id="element-10758" />

        {!noMotion && <motion.div className="absolute w-[500px] h-[500px] rounded-full pointer-events-none bg-white/70 blur-[80px]" initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: [0, 1.2, 1],
        opacity: [0, 1, 0.8]
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} data-id="element-10759" />}

        <ConfettiLayer particles={confetti.main} reduced={shouldReduceMotion} data-id="element-10760" />
        {(showExtraConfetti || streak >= 5) && <ConfettiLayer particles={confetti.extra} reduced={shouldReduceMotion} data-id="element-10761" />}

        <FloatingSparkles reduced={shouldReduceMotion} data-id="element-10762" />

        {/* Djay Avatar */}
        <motion.div initial={{
        scale: noMotion ? 1 : 0,
        y: noMotion ? 0 : 80,
        rotate: noMotion ? 0 : -15
      }} animate={{
        scale: 1,
        y: 0,
        rotate: 0
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 18,
        delay: noMotion ? 0 : 0.1
      }} className="relative z-10 mb-6" data-id="element-10763">
          <motion.div animate={noMotion ? {} : {
          y: [0, -12, 0],
          rotate: [0, -3, 3, 0]
        }} transition={{
          delay: 0.4,
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-10764">
            <DjayIdentity size="hero" emotion={emotion} animate={!noMotion} data-id="element-10765" />
          </motion.div>
        </motion.div>

        {/* Main text */}
        <motion.h1 initial={{
        scale: noMotion ? 1 : 0.8,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 30
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: noMotion ? 0 : 0.2
      }} className="text-4xl sm:text-6xl font-black text-amber-900 mb-4 tracking-tight text-center px-6 relative z-10" style={{
        textShadow: '0 4px 16px rgba(255, 255, 255, 0.6)'
      }} data-id="element-10766">
          {mainMessage}
        </motion.h1>

        {subMessage && <motion.p initial={{
        scale: noMotion ? 1 : 0.9,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: noMotion ? 0 : 0.3
      }} className="text-2xl sm:text-3xl font-bold text-amber-700 mb-8 text-center px-6 relative z-10" data-id="element-10767">
            {subMessage}
          </motion.p>}

        {/* Badges Choreography */}
        <motion.div initial="hidden" animate="visible" variants={{
        visible: {
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.4
          }
        }
      }} className="flex flex-col items-center gap-4 relative z-10" data-id="element-10768">
          <motion.div variants={{
          hidden: {
            opacity: 0,
            y: 20,
            scale: 0.8
          },
          visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              type: 'spring',
              stiffness: 400,
              damping: 20
            }
          }
        }} data-id="element-10769">
            <motion.div whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="flex items-center gap-3 px-8 py-4 rounded-full bg-white border-2 border-amber-300 shadow-xl shadow-amber-500/20" data-id="element-10770">
              <motion.div animate={{
              rotate: noMotion ? 0 : [0, 15, -15, 0],
              scale: noMotion ? 1 : [1, 1.2, 1]
            }} transition={{
              duration: noMotion ? 0 : 0.6,
              delay: noMotion ? 0 : 0.8,
              repeat: noMotion ? 0 : Infinity,
              repeatDelay: 2
            }} data-id="element-10771">
                <Star className="w-8 h-8 fill-amber-500 text-amber-500" data-id="element-10772" />
              </motion.div>
              <span className="text-3xl font-black text-amber-800" data-id="element-10773">
                +{xpDisplay} XP
              </span>
            </motion.div>
          </motion.div>

          {/* Mini Family Mastery Bar */}
          {familyMasteryPercent !== undefined && familyLabel && <motion.div variants={{
          hidden: {
            opacity: 0,
            y: 10,
            scale: 0.9
          },
          visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
              type: 'spring',
              stiffness: 400,
              damping: 20
            }
          }
        }} className="mt-2 flex flex-col items-center gap-2 bg-white/50 backdrop-blur-sm px-6 py-3 rounded-2xl border border-white/60" data-id="element-10774">
              <div className="text-xs font-black text-amber-800/70 uppercase tracking-widest" data-id="element-10775">
                {familyLabel}
              </div>
              <div className="w-40 h-2.5 bg-amber-200/50 rounded-full overflow-hidden shadow-inner" data-id="element-10776">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: `${familyMasteryPercent}%`
            }} transition={{
              duration: 1,
              delay: 0.8,
              ease: 'easeOut'
            }} className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full" data-id="element-10777" />
              </div>
            </motion.div>}

          <motion.div variants={{
          hidden: {
            opacity: 0,
            y: 10
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              type: 'spring',
              stiffness: 400,
              damping: 20
            }
          }
        }} className="flex gap-3 mt-2" data-id="element-10778">
            {streak >= 2 && <div className="px-4 py-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-500 text-white text-sm font-black shadow-lg shadow-orange-500/30 flex items-center gap-2 border border-orange-300/50" data-id="element-10779">
                🔥 {streak}x Combo!
              </div>}
            {priority === 'levelUp' && streakMilestone && <div className="px-4 py-2 rounded-full bg-orange-100 border-2 border-orange-200 text-orange-700 text-sm font-bold shadow-sm" data-id="element-10780">
                🔥 {streakMilestone.count} op rij
              </div>}
            {priority === 'levelUp' && dailyGoalReached && <div className="px-4 py-2 rounded-full bg-emerald-100 border-2 border-emerald-200 text-emerald-700 text-sm font-bold shadow-sm" data-id="element-10781">
                ✅ Dagelijks doel
              </div>}
          </motion.div>
        </motion.div>
      </motion.div>;
  }
  // ── Incorrect Splash ──
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeInOut'
  }} onClick={onComplete} className="min-h-[calc(100vh-80px)] w-full flex flex-col items-center justify-center overflow-hidden cursor-pointer relative rounded-3xl" style={{
    background: gradient
  }} data-id="element-10782">
      {!noMotion && <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white/80 blur-[80px] pointer-events-none" animate={{
      opacity: [0.4, 0.6, 0.4],
      scale: [1, 1.05, 1]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-10783" />}

      <WarmParticleLayer particles={WARM_PARTICLES} reduced={shouldReduceMotion} data-id="element-10784" />

      <motion.div initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : -20,
      scale: noMotion ? 1 : 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 25,
      delay: noMotion ? 0 : 0.1
    }} className="relative z-10 mb-8" data-id="element-10785">
        <motion.div animate={noMotion ? {} : {
        y: [0, -6, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-10786">
          <DjayIdentity size="hero" emotion={emotion} animate={!noMotion} data-id="element-10787" />
        </motion.div>
      </motion.div>

      <motion.h1 initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.2,
      duration: noMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="text-4xl sm:text-5xl font-black text-amber-950 mb-4 tracking-tight text-center px-6 relative z-10" data-id="element-10788">
        {mainMessage}
      </motion.h1>

      <motion.p initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 15
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.3,
      duration: noMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="text-xl sm:text-2xl text-amber-800/80 font-bold mb-10 text-center px-6 relative z-10" data-id="element-10789">
        {subMessage}
      </motion.p>

      <motion.div initial={{
      scale: noMotion ? 1 : 0.8,
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 15
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.4,
      type: 'spring',
      stiffness: 400,
      damping: 25
    }} className="flex flex-col items-center gap-3 relative z-10" data-id="element-10790">
        <div className="flex items-center gap-3 px-6 py-3.5 rounded-2xl bg-white/60 backdrop-blur-sm border-2 border-amber-200/50 shadow-lg shadow-amber-900/5" data-id="element-10791">
          <motion.div animate={noMotion ? {} : {
          rotate: [0, 10, -10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-10792">
            <Sparkles className="w-6 h-6 fill-amber-500 text-amber-500" data-id="element-10793" />
          </motion.div>
          <span className="text-2xl font-black text-amber-700" data-id="element-10794">
            +{xpDisplay} XP
          </span>
          <div className="w-px h-6 bg-amber-300/50 mx-1" data-id="element-10795" />
          <span className="text-sm font-black text-amber-600/80 uppercase tracking-widest" data-id="element-10796">
            voor de inzet
          </span>
        </div>
      </motion.div>
    </motion.div>;
}